// Users list — search + filter + sort. Rows link to the detail
// dashboard; per-row actions live there, not in the table.
//
// Routing note: `/users/$id` is a file-based child of this route, so
// TSR needs <Outlet /> for the detail page to mount. We use the same
// conditional-outlet pattern admins.tsx uses — on any non-list path
// under /users we return the Outlet alone so the detail page owns the
// screen.
import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { fetchUsers, keys } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Pagination } from "@/components/ui/Pagination";
import { SortableHeader } from "@/components/ui/SortableHeader";
import { Avatar } from "@/components/ui/Avatar";
import { useTableState } from "@/lib/useTableState";
import { fmtRelative, statusLabel } from "@/lib/format";

export const Route = createFileRoute("/_app/users")({
  component: UsersPage,
});

interface UserFilters {
  status: string;
  kyc_level: string;
  role: string;
}

function UsersPage() {
  const { location } = useRouterState();
  // /users is the list; anything deeper (e.g. /users/<id>) is a child
  // route whose component renders via <Outlet />.
  if (location.pathname !== "/users") return <Outlet />;
  return <UsersListPage />;
}

function UsersListPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const table = useTableState<UserFilters>({
    defaultSort: { by: "created_at", dir: "desc" },
    defaultFilters: { status: "", kyc_level: "", role: "" },
  });
  // Reset to page 0 whenever the search filter changes — otherwise a
  // narrowed result-set could leave us paginated past the new total.
  useEffect(() => {
    table.setPage(0);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const trimmed = q.trim();
  const filtersForKey = {
    status: table.filters.status || undefined,
    kyc_level: table.filters.kyc_level || undefined,
    role: table.filters.role || undefined,
  };
  const users = useQuery({
    queryKey: keys.usersList(trimmed, table.limit, table.offset, table.sort, filtersForKey),
    queryFn: () =>
      fetchUsers({
        limit: table.limit,
        offset: table.offset,
        q: trimmed || undefined,
        sort: table.sort,
        status: table.filters.status || undefined,
        kyc_level: table.filters.kyc_level || undefined,
        role: table.filters.role || undefined,
      }),
  });

  const openDetail = (id: string) => navigate({ to: "/users/$id", params: { id } });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Users</h1>
        <p className="text-sm text-muted">
          Search by user ID, display name, or email. Open a row to manage suspension, sessions, and review the
          full account dashboard.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[260px] flex-1">
          <Input
            placeholder="Search by ID, name, or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="w-40">
          <Select
            placeholder="Status — any"
            value={table.filters.status}
            onChange={(e) => table.setFilter("status", e.target.value)}
            options={[
              { value: "ACTIVE", label: "Active" },
              { value: "SUSPENDED", label: "Suspended" },
              { value: "CLOSED", label: "Closed" },
            ]}
          />
        </div>
        <div className="w-40">
          <Select
            placeholder="KYC — any"
            value={table.filters.kyc_level}
            onChange={(e) => table.setFilter("kyc_level", e.target.value)}
            options={[
              { value: "NONE", label: "None" },
              { value: "BASIC", label: "Basic" },
              { value: "ADVANCED", label: "Advanced" },
            ]}
          />
        </div>
        <div className="w-40">
          <Select
            placeholder="Role — any"
            value={table.filters.role}
            onChange={(e) => table.setFilter("role", e.target.value)}
            options={[
              { value: "USER", label: "User" },
              { value: "TRADER", label: "Trader" },
              { value: "VIP", label: "VIP" },
            ]}
          />
        </div>
        {(table.hasActiveFilters || q) && (
          <Button
            variant="ghost"
            onClick={() => {
              setQ("");
              table.reset();
            }}
          >
            Reset
          </Button>
        )}
      </div>
      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Accounts</CardTitle>
          <span className="text-2xs text-muted">{users.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {users.isLoading && (
            <div className="space-y-2 p-5">
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}
            </div>
          )}
          {users.data && users.data.items.length === 0 && (
            <p className="px-5 py-16 text-center text-sm text-muted">No users match the current filters.</p>
          )}
          {users.data && users.data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <SortableHeader label="Display name" field="display_name" sort={table.sort} onSortChange={table.setSort} className="px-5" />
                    <SortableHeader label="Email" field="email" sort={table.sort} onSortChange={table.setSort} />
                    <SortableHeader label="Status" field="status" sort={table.sort} onSortChange={table.setSort} />
                    <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">KYC</th>
                    <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">Roles</th>
                    <SortableHeader label="Last seen" field="last_seen_at" sort={table.sort} onSortChange={table.setSort} align="right" />
                    <SortableHeader label="Created" field="created_at" sort={table.sort} onSortChange={table.setSort} align="right" className="px-5" />
                  </tr>
                </thead>
                <tbody>
                  {users.data.items.map((u) => (
                    <tr
                      key={u.user_id}
                      onClick={() => openDetail(u.user_id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openDetail(u.user_id);
                        }
                      }}
                      tabIndex={0}
                      role="link"
                      aria-label={`Open ${u.email}`}
                      className="cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-neon/5 focus:bg-neon/5 focus:outline-none"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar url={u.avatar_url} name={u.display_name || u.email} />
                          <div className="min-w-0">
                            <div className="truncate text-xs font-medium text-fg">{u.display_name || "—"}</div>
                            <div className="truncate font-mono text-2xs text-muted">{u.user_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-fg">{u.email}</td>
                      <td className="px-3 py-3">
                        <Badge tone={statusTone(u.status)}>{statusLabel(u.status)}</Badge>
                      </td>
                      <td className="px-3 py-3 text-2xs uppercase tracking-wide text-muted">{u.kyc_level ?? "—"}</td>
                      <td className="px-3 py-3 text-2xs text-muted">{(u.roles ?? []).join(", ") || "—"}</td>
                      <td className="px-3 py-3 text-right text-2xs text-muted">{fmtRelative(u.last_seen_at)}</td>
                      <td className="px-5 py-3 text-right text-2xs text-muted">{fmtRelative(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {users.data && (
            <Pagination
              page={users.data.page}
              currentPage={table.page}
              onPageChange={table.setPage}
              itemLabel="users"
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function statusTone(s: string): "success" | "warning" | "danger" | "muted" {
  switch (s) {
    case "ACTIVE":
      return "success";
    case "SUSPENDED":
      return "warning";
    case "CLOSED":
      return "danger";
    default:
      return "muted";
  }
}

